(function (global) {
  'use strict';

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[char]);

  class PMSBeltBuilder {
    constructor(root) {
      this.root = root;
      this.config = this.readJson('[data-pms-belt-config]') || {};
      this.core = global.PMSBeltBuilderCore;
      this.adapter = new global.PMSBeltBuilderShopify(root);
      this.programs = this.adapter.loadPrograms();
      const initialId = this.config.defaultProgramId || this.programs[0]?.id || '';
      this.state = this.core.createState(initialId);
      this.status = '';
      this.statusError = false;
      this.busy = false;
    }

    readJson(selector) {
      const node = this.root.querySelector(selector);
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (_) { return null; }
    }

    get program() {
      return this.programs.find((program) => program.id === this.state.programId) || null;
    }

    init() {
      if (!this.core || !global.PMSBeltBuilderShopify) return;
      this.root.addEventListener('change', (event) => this.onChange(event));
      this.root.addEventListener('click', (event) => this.onClick(event));
      this.render();
    }

    onChange(event) {
      const field = event.target?.dataset?.field;
      if (!field) return;
      if (field === 'program') {
        this.state = this.core.createState(event.target.value);
      } else if (field === 'buckle') {
        this.state.buckleVariantId = event.target.value;
      } else if (field === 'strap') {
        this.state.strapVariantId = event.target.value;
      } else if (field === 'sizing') {
        this.state.sizing = event.target.value;
        if (event.target.value !== 'cut') {
          this.state.cutSize = '';
          this.state.acknowledged = false;
        }
      } else if (field === 'cut-size') {
        this.state.cutSize = event.target.value;
      } else if (field === 'acknowledged') {
        this.state.acknowledged = event.target.checked;
      }
      this.clearStatus();
      this.render();
    }

    onClick(event) {
      const button = event.target.closest('[data-action]');
      if (!button) return;
      if (button.dataset.action === 'add-belt') this.addBelt();
    }

    clearStatus() {
      this.status = '';
      this.statusError = false;
    }

    money(cents) {
      return new Intl.NumberFormat(this.config.locale || 'en-US', {
        style: 'currency', currency: this.config.currency || 'USD',
      }).format((Number(cents) || 0) / 100);
    }

    inventoryNote(status) {
      if (!status.tracked) return 'Available';
      if (status.availableNow > 0) return `${status.availableNow} available now`;
      if (status.continueSelling) return 'Available to order';
      return 'Unavailable';
    }

    visualPicker(field, choices, selectedId, emptyMessage) {
      if (!choices.length) return `<div class="pms-belt__notice is-error">${escapeHtml(emptyMessage)}</div>`;
      return `<div class="pms-belt__visual-grid">${choices.map((choice) => {
        const selected = String(choice.id) === String(selectedId);
        const image = choice.image
          ? `<img src="${escapeHtml(choice.image)}" alt="${escapeHtml(choice.title)}" loading="lazy">`
          : '<span class="pms-belt__placeholder">No image</span>';
        return `<label class="pms-belt__choice ${selected ? 'is-selected' : ''} ${!choice.available ? 'is-unavailable' : ''}">
          <input type="radio" name="pms-belt-${escapeHtml(field)}" data-field="${escapeHtml(field)}" value="${escapeHtml(choice.id)}" ${selected ? 'checked' : ''} ${!choice.available ? 'disabled' : ''}>
          <span class="pms-belt__image">${image}</span>
          <span class="pms-belt__copy"><strong>${escapeHtml(choice.title)}</strong><small>${escapeHtml(choice.subtitle)}</small><em>${escapeHtml(this.inventoryNote(choice.status))}</em><b>${this.money(choice.price)}</b></span>
          <span class="pms-belt__check" aria-hidden="true">&#10003;</span>
        </label>`;
      }).join('')}</div>`;
    }

    programPicker() {
      if (this.programs.length <= 1) return '';
      return `<fieldset class="pms-belt__programs"><legend>Choose your belt system</legend><div>${this.programs.map((program) => `
        <label class="${program.id === this.state.programId ? 'is-selected' : ''}">
          <input type="radio" name="pms-belt-program" data-field="program" value="${escapeHtml(program.id)}" ${program.id === this.state.programId ? 'checked' : ''}>
          ${program.badge ? `<small>${escapeHtml(program.badge)}</small>` : ''}<strong>${escapeHtml(program.title)}</strong><span>${escapeHtml(program.description)}</span>
        </label>`).join('')}</div></fieldset>`;
    }

    sizes() {
      const values = [];
      const min = Number(this.config.sizeMin || 28);
      const max = Number(this.config.sizeMax || 60);
      const step = Math.max(1, Number(this.config.sizeStep || 2));
      for (let size = min; size <= max; size += step) values.push(String(size));
      return values;
    }

    sizingPanel() {
      if (this.config.allowCustomCut === false) return '';
      return `<fieldset class="pms-belt__sizing"><legend>A note about belt sizing</legend>
        <p>We recommend leaving the strap uncut until the final fit is confirmed.</p>
        <label><input type="radio" name="pms-belt-sizing" data-field="sizing" value="uncut" ${this.state.sizing !== 'cut' ? 'checked' : ''}><span><strong>Leave strap uncut — recommended</strong><small>It can be sized in-store or at home and remains eligible for return under the normal policy.</small></span></label>
        <label><input type="radio" name="pms-belt-sizing" data-field="sizing" value="cut" ${this.state.sizing === 'cut' ? 'checked' : ''}><span><strong>Cut strap to size</strong><small>Once custom-cut, the strap is final sale and cannot be returned or exchanged.</small></span></label>
        ${this.state.sizing === 'cut' ? `<div class="pms-belt__cut">
          <label><span>Requested cut size</span><select data-field="cut-size"><option value="">Choose a size</option>${this.sizes().map((size) => `<option value="${size}" ${size === this.state.cutSize ? 'selected' : ''}>${size}</option>`).join('')}</select></label>
          <label class="pms-belt__ack"><input type="checkbox" data-field="acknowledged" ${this.state.acknowledged ? 'checked' : ''}><span>I understand that this custom-cut belt will be final sale.</span></label>
        </div>` : ''}
      </fieldset>`;
    }

    review() {
      const program = this.program;
      if (!program) return '';
      const validation = this.core.validate({ state: this.state, program, catalog: program.catalog });
      const buckle = validation.buckle;
      const strap = validation.strap;
      const total = Number(buckle?.price || 0) + Number(strap?.price || 0);
      return `<aside class="pms-belt__review">
        <p>Your belt</p><h3>${escapeHtml(program.title)}</h3>
        <dl><div><dt>Buckle</dt><dd>${escapeHtml(buckle?.title || 'Not selected')}</dd></div><div><dt>Strap</dt><dd>${escapeHtml(strap?.title || 'Not selected')}</dd></div><div><dt>Sizing</dt><dd>${escapeHtml(this.state.sizing === 'cut' ? `Cut to ${this.state.cutSize || '—'}` : 'Leave uncut')}</dd></div></dl>
        <div class="pms-belt__total"><span>Complete belt total</span><strong>${this.money(total)}</strong></div>
        ${!validation.valid ? `<div class="pms-belt__needed">${escapeHtml(validation.errors.join(' '))}</div>` : ''}
        <button type="button" class="button" data-action="add-belt" ${!validation.valid || this.busy ? 'disabled' : ''}>${this.busy ? 'Adding belt…' : escapeHtml(this.config.buttonLabel || 'Add Complete Belt to Cart')}</button>
        ${this.status ? `<div class="pms-belt__status ${this.statusError ? 'is-error' : ''}" aria-live="polite">${escapeHtml(this.status)}</div>` : ''}
      </aside>`;
    }

    render() {
      const app = this.root.querySelector('[data-pms-belt-app]');
      if (!app) return;
      if (!this.programs.length) {
        app.innerHTML = '<div class="pms-belt__notice is-error">No belt program is configured. Choose buckle and strap collections in the theme editor.</div>';
        return;
      }
      const program = this.program;
      const buckleChoices = this.core.simpleChoices(program.catalog.buckles, [/buckle/i, /style/i]);
      const strapChoices = this.core.simpleChoices(program.catalog.straps, [/strap/i, /style/i, /colou?r/i, /leather/i]);
      app.innerHTML = `${this.programPicker()}
        <div class="pms-belt__layout"><main>
          ${program.badge ? `<p class="pms-belt__badge">${escapeHtml(program.badge)}</p>` : ''}
          <h2>${escapeHtml(program.title)}</h2><p class="pms-belt__description">${escapeHtml(program.description)}</p>
          <section><h3>1. Choose your buckle</h3><p>Click a picture to select the buckle style.</p>${this.visualPicker('buckle', buckleChoices, this.state.buckleVariantId, 'No eligible buckles are configured.')}</section>
          <section><h3>2. Choose your strap</h3><p>Click a picture to select the strap color and material.</p>${this.visualPicker('strap', strapChoices, this.state.strapVariantId, 'No eligible straps are configured.')}</section>
          ${this.sizingPanel()}
          ${program.warranty ? `<div class="pms-belt__warranty"><strong>Built to last</strong><span>${escapeHtml(program.warranty)}</span></div>` : ''}
        </main>${this.review()}</div>`;
    }

    async addBelt() {
      if (this.busy || !this.program) return;
      let plan;
      try { plan = this.core.buildCartPlan({ state: this.state, program: this.program, catalog: this.program.catalog }); }
      catch (error) {
        this.status = error.message;
        this.statusError = true;
        this.render();
        return;
      }
      this.busy = true;
      this.status = '';
      this.render();
      try {
        await this.adapter.addCartPlan(plan, this.config.afterAdd || 'cart');
        this.status = 'Your complete belt was added to the cart.';
      } catch (error) {
        this.status = error.message || 'The belt could not be added to the cart.';
        this.statusError = true;
      } finally {
        this.busy = false;
        this.render();
      }
    }
  }

  const boot = () => document.querySelectorAll('[data-pms-belt-builder]').forEach((root) => {
    if (root.dataset.pmsBeltReady) return;
    root.dataset.pmsBeltReady = 'true';
    new PMSBeltBuilder(root).init();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})(typeof window !== 'undefined' ? window : globalThis);
